import React, { createContext, useContext, useLayoutEffect, useState } from "react";

type ScaleInfo = { scale: number; viewportHeight: number; effectiveWidth: number };

const DEFAULT_WIDTH = typeof window !== "undefined" ? window.innerWidth : 1440;

const ScaleContext = createContext<ScaleInfo>({
    scale: 1,
    viewportHeight: typeof window !== "undefined" ? window.innerHeight : 800,
    effectiveWidth: DEFAULT_WIDTH,
});

// Lets section-height math (see useZoomAwareSectionHeight in BuilderSections.tsx)
// know the current zoom factor — vh units are relative to the real viewport and
// are NOT affected by CSS zoom, so a "100vh" section would otherwise keep its
// full real on-screen height while its zoomed content shrinks inside it.
//
// `effectiveWidth` is the real device width being simulated (or the admin's
// forced preview width) — grid column counts and minimum font sizes should key
// off this instead of a CSS breakpoint, since a real `sm:`/`md:` class reads the
// admin's actual (wide) browser window, not the narrow forced-width preview frame,
// and would silently fail to collapse to a mobile layout inside that preview.
export const useResponsiveScale = () => useContext(ScaleContext);

// The builder positions everything in px/% against a fixed "design width" desktop
// canvas. Rather than re-authoring every section per breakpoint, this scales the
// whole design down to fit whatever width it's given — real device width on the
// public site, or a forced phone width for the admin's mobile preview — so
// proportions (text, images, spacing) all shrink together instead of breaking.
//
// Uses CSS `zoom` rather than `transform: scale()` on purpose: zoom actually
// reflows layout, so the container's height naturally matches the shrunk content
// with no manual measuring. transform is paint-only, so an element's contribution
// to its parent's height stays at its *unscaled* size — every "measure scrollHeight
// and set an explicit height" workaround for that is fragile against async content
// (carousels, images loading) and drifts out of sync, leaving stale gaps or clipped
// content. `zoom` is supported by every engine mobile visitors actually use
// (Chrome/Safari/Edge); only desktop Firefox lacks it, and desktop never scales
// here anyway (scale is 1 above the design width).
const ResponsiveScaler: React.FC<{ children: React.ReactNode; designWidth?: number; forceWidth?: number }> = ({
    children,
    designWidth = 1440,
    forceWidth,
}) => {
    // Computed synchronously from the real (or forced) width right away —
    // NOT hardcoded to scale:1 pending a later effect. That earlier version
    // always painted the "scale>=1, no zoom" branch first regardless of the
    // real width, then corrected itself in a useEffect (which runs AFTER
    // paint). For any forceWidth under 1440 that meant a full remount onto
    // the completely different zoom-branch JSX shortly after mount — which
    // incidentally re-ran every descendant's mount-time logic a second time,
    // late enough that transient states (e.g. a Dialog's own open transition)
    // had settled. Desktop/large-desktop widths never took that second branch
    // (scale is >=1 either way), so anything relying on an accurate
    // mount-time measurement — like BuilderSections.tsx's scroll-reveal
    // "is this already on screen?" check — kept whatever wrong reading it got
    // from the very first, possibly-mid-transition render, with nothing to
    // ever correct it.
    const computeInfo = (): ScaleInfo => {
        const vw = forceWidth ?? (typeof window !== "undefined" ? window.innerWidth : DEFAULT_WIDTH);
        const vh = typeof window !== "undefined" ? window.innerHeight : 800;
        return {
            scale: vw < designWidth ? vw / designWidth : 1,
            viewportHeight: vh,
            effectiveWidth: vw,
        };
    };

    const [info, setInfo] = useState<ScaleInfo>(computeInfo);

    // useLayoutEffect (not useEffect): resize recalculation must land before
    // paint too, for the same reason as the initial computation above — a
    // post-paint correction is exactly the "wrong reading persists" gap this
    // fix closes.
    useLayoutEffect(() => {
        setInfo(computeInfo());
        if (forceWidth) return undefined;
        const recalcScale = () => setInfo(computeInfo());
        window.addEventListener("resize", recalcScale);
        return () => window.removeEventListener("resize", recalcScale);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [designWidth, forceWidth]);

    const { scale } = info;

    // At/above the design width: cap and center at designWidth instead of letting
    // it stretch full-bleed across whatever the real container happens to be.
    // Elements are positioned by x/y% *of the design canvas* — on an uncapped
    // wide container (a real desktop monitor's full browser width, which varies
    // person to person and is usually well over 1440px) that same % lands at a
    // different absolute pixel offset than it does in a narrower context (the
    // admin's Preview dialog, the editor's own canvas), so the exact same saved
    // layout visibly shifted between them. No `zoom` needed here (scale is 1,
    // nothing to shrink) — a plain max-width + centering wrapper doesn't break
    // `position: sticky` since it sets no overflow/transform of its own.
    if (scale >= 1) {
        return (
            <ScaleContext.Provider value={info}>
                <div style={{ maxWidth: `${designWidth}px`, margin: "0 auto" }}>{children}</div>
            </ScaleContext.Provider>
        );
    }

    const innerStyle: React.CSSProperties & { zoom?: number } = { width: `${designWidth}px`, zoom: scale };

    return (
        <ScaleContext.Provider value={info}>
            {/* No overflow:hidden here (unlike it might seem needed to clip the
                zoomed 1440px-wide inner div): `zoom` already scales the box's
                RENDERED size, not just its paint, so width*scale == vw exactly
                by construction (scale is computed as vw/designWidth) — nothing
                actually overflows. overflow:hidden was a defensive leftover,
                but any overflow/scroll/auto ancestor makes it the reference
                container for position:sticky descendants (position:sticky
                elements only stick within their nearest such ancestor, per
                spec) — this one wraps the ENTIRE page, so it silently broke
                the sticky header on every screen under 1440px wide (i.e.
                virtually everyone) the moment `sticky` was added anywhere
                inside it. */}
            <div style={{ width: forceWidth ? `${forceWidth}px` : "100%", margin: forceWidth ? "0 auto" : undefined }}>
                <div style={innerStyle}>{children}</div>
            </div>
        </ScaleContext.Provider>
    );
};

export default ResponsiveScaler;
